import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, Edit, Trash2, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { payrollPaymentService } from '@/services/payrollPaymentService'
import type { PayrollPaymentListItem } from '@/api/types/payroll.types'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function formatMonthYear(ym?: string): string {
  if (!ym || ym.length < 5) return '—'
  // API may return MM-YYYY or YYYY-MM
  const parts = ym.includes('-') ? ym.split('-') : []
  if (parts.length >= 2) {
    const m = parseInt(parts[0], 10)
    const y = parts[1]
    if (m >= 1 && m <= 12) return `${MONTH_NAMES[m - 1]} ${y}`
    const m2 = parseInt(parts[1], 10)
    const y2 = parts[0]
    if (m2 >= 1 && m2 <= 12) return `${MONTH_NAMES[m2 - 1]} ${y2}`
  }
  return ym
}

function formatDate(dateString?: string): string {
  if (!dateString) return '—'
  try {
    const d = new Date(dateString)
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(dateString).slice(0, 10) || '—'
  }
}

export const PayrollPaymentList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [rows, setRows] = useState<PayrollPaymentListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalRecords(0)
      return
    }
    let cancelled = false
    setLoading(true)
    payrollPaymentService
      .getAll(branchId, pageNumber, pageSize)
      .then((res) => {
        if (!cancelled) {
          setRows(res.data ?? [])
          setTotalRecords(res.totalRecords ?? 0)
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setRows([])
          setTotalRecords(0)
          const msg = (err.response?.data?.message || err.message || '').toLowerCase()
          if (msg.includes('no payroll payment') || msg.includes('not found') || msg.includes('no data')) {
            return
          }
          toast.error(err.response?.data?.message || err.message || 'Failed to load payroll payments')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [branchId, pageNumber, pageSize])

  const filteredRows = React.useMemo(() => {
    if (!search.trim()) return rows
    const q = search.toLowerCase()
    return rows.filter(
      (r) =>
        (r.paymentNo ?? '').toLowerCase().includes(q) ||
        formatMonthYear(r.payrollMonthYear).toLowerCase().includes(q) ||
        formatDate(r.date ?? r.paymentDate).toLowerCase().includes(q) ||
        String(r.amount ?? '').includes(q)
    )
  }, [rows, search])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))

  const handleDelete = async (row: PayrollPaymentListItem) => {
    if (!row.id) return
    const ok = await showDeleteConfirm(undefined, 'Are you sure you want to delete this payroll payment?')
    if (!ok) return
    try {
      await payrollPaymentService.delete(row.id)
      await showSuccess('Deleted', 'Payroll payment deleted successfully.', 2000)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      setTotalRecords((prev) => Math.max(0, prev - 1))
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to delete payroll payment.')
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title="Payroll Payment" description="Payroll payment list" />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Please select a branch to view payroll payments.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title="Payroll Payment" description="Payroll payment list">
        <Button variant="default" onClick={() => navigate('/payroll/payroll-payment/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Payroll Payment
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Payroll Payments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="rounded-md border overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">Payment No</TableHead>
                    <TableHead className="text-center">Month/Year</TableHead>
                    <TableHead className="text-center">Payment Date</TableHead>
                    <TableHead className="text-center">Amount</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No payroll payments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="text-center">{row.paymentNo ?? '—'}</TableCell>
                        <TableCell className="text-center">{formatMonthYear(row.payrollMonthYear)}</TableCell>
                        <TableCell className="text-center">{formatDate(row.date ?? row.paymentDate)}</TableCell>
                        <TableCell className="text-center">{row.amount ?? '—'}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/payroll/payroll-payment/${row.id}/edit`)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(row)}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
            <span>
              Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, totalRecords)} of {totalRecords} entries
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span>Page {pageNumber} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
